'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import AnalyzingOverlay from './AnalyzingOverlay';

interface LocationData {
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  timezone: string;
}

interface BirthFormValues {
  birthDate: string;
  birthTime: string;
  birthLocation: string;
  gender: 'male' | 'female';
  timeUncertain: boolean;
}

interface CityOption {
  label: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  timezone: string;
}

const TAIWAN_CITIES: CityOption[] = [
  { label: '台灣・台北市', city: '台北市', country: '台灣', latitude: 25.0330, longitude: 121.5654, timezone: 'Asia/Taipei' },
  { label: '台灣・新北市', city: '新北市', country: '台灣', latitude: 25.0169, longitude: 121.4627, timezone: 'Asia/Taipei' },
  { label: '台灣・桃園市', city: '桃園市', country: '台灣', latitude: 24.9937, longitude: 121.3010, timezone: 'Asia/Taipei' },
  { label: '台灣・桃園市・中壢區', city: '中壢區', country: '台灣', latitude: 24.9670, longitude: 121.2249, timezone: 'Asia/Taipei' },
  { label: '台灣・桃園市・龍潭區', city: '龍潭區', country: '台灣', latitude: 24.8741, longitude: 121.2199, timezone: 'Asia/Taipei' },
  { label: '台灣・桃園市・平鎮區', city: '平鎮區', country: '台灣', latitude: 24.9459, longitude: 121.2157, timezone: 'Asia/Taipei' },
  { label: '台灣・台中市', city: '台中市', country: '台灣', latitude: 24.1477, longitude: 120.6736, timezone: 'Asia/Taipei' },
  { label: '台灣・台南市', city: '台南市', country: '台灣', latitude: 22.9999, longitude: 120.2269, timezone: 'Asia/Taipei' },
  { label: '台灣・高雄市', city: '高雄市', country: '台灣', latitude: 22.6273, longitude: 120.3014, timezone: 'Asia/Taipei' },
  { label: '台灣・新竹市', city: '新竹市', country: '台灣', latitude: 24.8138, longitude: 120.9675, timezone: 'Asia/Taipei' },
  { label: '台灣・新竹縣', city: '新竹縣', country: '台灣', latitude: 24.8387, longitude: 121.0177, timezone: 'Asia/Taipei' },
  { label: '台灣・基隆市', city: '基隆市', country: '台灣', latitude: 25.1276, longitude: 121.7392, timezone: 'Asia/Taipei' },
  { label: '台灣・宜蘭縣', city: '宜蘭縣', country: '台灣', latitude: 24.7021, longitude: 121.7377, timezone: 'Asia/Taipei' },
  { label: '台灣・花蓮縣', city: '花蓮縣', country: '台灣', latitude: 23.9871, longitude: 121.6015, timezone: 'Asia/Taipei' },
  { label: '台灣・台東縣', city: '台東縣', country: '台灣', latitude: 22.7583, longitude: 121.1444, timezone: 'Asia/Taipei' },
  { label: '台灣・苗栗縣', city: '苗栗縣', country: '台灣', latitude: 24.5602, longitude: 120.8214, timezone: 'Asia/Taipei' },
  { label: '台灣・彰化縣', city: '彰化縣', country: '台灣', latitude: 24.0518, longitude: 120.5161, timezone: 'Asia/Taipei' },
  { label: '台灣・南投縣', city: '南投縣', country: '台灣', latitude: 23.9609, longitude: 120.9718, timezone: 'Asia/Taipei' },
  { label: '台灣・雲林縣', city: '雲林縣', country: '台灣', latitude: 23.7092, longitude: 120.4313, timezone: 'Asia/Taipei' },
  { label: '台灣・嘉義市', city: '嘉義市', country: '台灣', latitude: 23.4801, longitude: 120.4491, timezone: 'Asia/Taipei' },
  { label: '台灣・嘉義縣', city: '嘉義縣', country: '台灣', latitude: 23.4518, longitude: 120.2554, timezone: 'Asia/Taipei' },
  { label: '台灣・屏東縣', city: '屏東縣', country: '台灣', latitude: 22.5519, longitude: 120.5487, timezone: 'Asia/Taipei' },
  { label: '台灣・澎湖縣', city: '澎湖縣', country: '台灣', latitude: 23.5711, longitude: 119.5793, timezone: 'Asia/Taipei' },
  { label: '日本・東京', city: '東京', country: '日本', latitude: 35.6762, longitude: 139.6503, timezone: 'Asia/Tokyo' },
  { label: '日本・大阪', city: '大阪', country: '日本', latitude: 34.6937, longitude: 135.5023, timezone: 'Asia/Tokyo' },
  { label: '中國・上海', city: '上海', country: '中國', latitude: 31.2304, longitude: 121.4737, timezone: 'Asia/Shanghai' },
  { label: '中國・北京', city: '北京', country: '中國', latitude: 39.9042, longitude: 116.4074, timezone: 'Asia/Shanghai' },
  { label: '香港', city: '香港', country: '香港', latitude: 22.3193, longitude: 114.1694, timezone: 'Asia/Hong_Kong' },
  { label: '新加坡', city: '新加坡', country: '新加坡', latitude: 1.3521, longitude: 103.8198, timezone: 'Asia/Singapore' },
];

export default function BirthDataForm() {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BirthFormValues>({
    defaultValues: {
      gender: 'female',
      timeUncertain: false,
    },
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const timeUncertain = watch('timeUncertain');
  const gender = watch('gender');

  const filteredCities = locationQuery.length > 0
    ? TAIWAN_CITIES.filter((c) =>
        c.label.includes(locationQuery) || c.city.includes(locationQuery)
      )
    : [];

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocationQuery(val);
    setValue('birthLocation', val);
    setShowSuggestions(true);
    if (!val) setSelectedLocation(null);
  };

  const handleSelectCity = (option: CityOption) => {
    setLocationQuery(option.label);
    setValue('birthLocation', option.label);
    setSelectedLocation({
      city: option.city,
      country: option.country,
      latitude: option.latitude,
      longitude: option.longitude,
      timezone: option.timezone,
    });
    setShowSuggestions(false);
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  const handleInputBlur = () => {
    // Delay to allow touch events on suggestion items to fire first
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const onSubmit = async (data: BirthFormValues) => {
    setIsAnalyzing(true);

    // Build the bazi input payload from form values + resolved location
    const baziInput = {
      birthDate: data.birthDate,
      birthTime: data.timeUncertain ? '12:00' : data.birthTime,
      latitude: selectedLocation?.latitude ?? null,
      longitude: selectedLocation?.longitude ?? null,
      timezone: selectedLocation?.timezone ?? 'Asia/Taipei',
      gender: data.gender,
      // Extra display fields
      locationLabel: locationQuery,
      city: selectedLocation?.city ?? '',
      country: selectedLocation?.country ?? '',
      timeUncertain: data.timeUncertain,
    };

    // Persist to sessionStorage so the results page can read it
    try {
      sessionStorage.setItem('birthDataInput', JSON.stringify(baziInput));
    } catch {
      // sessionStorage unavailable — proceed anyway; results page will fall back to demo data
    }

    // Brief animation delay for the analyzing overlay
    await new Promise((resolve) => setTimeout(resolve, 3000));
    router.push('/personal-results-page');
  };

  // SSR skeleton — deterministic, no conditional class names based on state
  if (!mounted) {
    return (
      <div className="flex flex-col gap-6">
        {/* Birth Date */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            出生年月日
            <span className="text-element-fire ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-1">格式：YYYY / MM / DD</p>
          <div className="w-full h-12 rounded-xl border border-border bg-card" />
        </div>

        {/* Birth Time */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            出生時間
            <span className="text-element-fire ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-1">時間越精確，分析越準確。</p>
          <div className="w-full h-12 rounded-xl border border-border bg-card" />
          <label className="flex items-center gap-3 cursor-pointer mt-1">
            <div className="w-5 h-5 rounded-md border-2 border-border bg-card" />
            <span className="text-sm text-muted-foreground">時間不確定</span>
          </label>
        </div>

        {/* Birth Location */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            出生地
            <span className="text-element-fire ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-1">城市或地區即可，如「台北市」</p>
          <div className="w-full h-12 rounded-xl border border-border bg-card" />
        </div>

        {/* Gender */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            性別
            <span className="text-element-fire ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-1">
            八字分析中，性別影響五行能量的流動方向。
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="h-14 rounded-xl border-2 border-accent bg-accent/8" />
            <div className="h-14 rounded-xl border-2 border-border bg-card" />
          </div>
        </div>

        {/* Privacy note */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary/60">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0 mt-0.5">
            <circle cx="8" cy="8" r="7" stroke="var(--muted-foreground)" strokeWidth="1.2" />
            <path d="M8 7v4M8 5.5v.5" stroke="var(--muted-foreground)" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <p className="text-xs text-muted-foreground leading-relaxed">
            你的出生資料僅用於計算五行比例，不會與第三方共享。
            分析完成後，你可以選擇是否儲存結果。
          </p>
        </div>

        {/* Submit */}
        <button
          type="button"
          disabled
          className="w-full h-14 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-wide flex items-center justify-center gap-3 mt-2 opacity-80"
        >
          分析我的五行色彩
        </button>
      </div>
    );
  }

  return (
    <>
      {isAnalyzing && <AnalyzingOverlay />}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        {/* Birth Date */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            出生年月日
            <span className="text-element-fire ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-1">格式：YYYY / MM / DD</p>
          <input
            type="date"
            {...register('birthDate', { required: '請輸入出生年月日' })}
            className="w-full h-12 px-4 rounded-xl border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-accent transition-all duration-200"
          />
          {errors.birthDate && (
            <p className="text-xs text-element-fire mt-0.5">{errors.birthDate.message}</p>
          )}
        </div>

        {/* Birth Time */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            出生時間
            {!timeUncertain && <span className="text-element-fire ml-1">*</span>}
          </label>
          <p className="text-xs text-muted-foreground -mt-1">
            時間越精確，分析越準確。
          </p>
          <input
            type="time"
            disabled={timeUncertain}
            {...register('birthTime', {
              required: timeUncertain ? false : '請輸入出生時間',
            })}
            className="w-full h-12 px-4 rounded-xl border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-accent transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          />
          {errors.birthTime && (
            <p className="text-xs text-element-fire mt-0.5">{errors.birthTime.message}</p>
          )}

          {/* Time uncertain checkbox */}
          <label className="flex items-center gap-3 cursor-pointer mt-1 group">
            <div className="relative">
              <input
                type="checkbox"
                {...register('timeUncertain')}
                className="sr-only"
              />
              <div
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                  timeUncertain
                    ? 'border-accent bg-accent' :'border-border bg-card group-hover:border-accent/60'
                }`}
              >
                {timeUncertain && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4l3 3 5-6" stroke="var(--primary-foreground)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-muted-foreground">時間不確定</span>
          </label>
        </div>

        {/* Birth Location */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            出生地
            <span className="text-element-fire ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-1">城市或地區即可，如「台北市」</p>

          {/* Hidden field for react-hook-form validation */}
          <input
            type="hidden"
            {...register('birthLocation', { required: '請輸入出生地' })}
          />

          {/* Visible controlled input — NOT using register spread to avoid conflict */}
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="搜尋城市..."
              value={locationQuery}
              onChange={handleLocationChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              className="w-full h-12 px-4 rounded-xl border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-accent transition-all duration-200 relative z-10"
            />

            {showSuggestions && filteredCities.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden"
                style={{ zIndex: 50 }}
              >
                {filteredCities.slice(0, 7).map((option) => (
                  <button
                    key={`city-${option.label}`}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectCity(option);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      handleSelectCity(option);
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-secondary active:bg-secondary transition-colors duration-150 border-b border-border/40 last:border-b-0"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {errors.birthLocation && (
            <p className="text-xs text-element-fire mt-0.5">{errors.birthLocation.message}</p>
          )}
        </div>

        {/* Gender */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            性別
            <span className="text-element-fire ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-1">
            八字分析中，性別影響五行能量的流動方向。
          </p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: 'female', label: '女', sub: 'Female' },
              { value: 'male', label: '男', sub: 'Male' },
            ].map((opt) => (
              <button
                key={`gender-${opt.value}`}
                type="button"
                onClick={() => setValue('gender', opt.value as 'male' | 'female')}
                className={`h-14 rounded-xl border-2 flex flex-col items-center justify-center gap-0.5 transition-all duration-200 ${
                  gender === opt.value
                    ? 'border-accent bg-accent/8 text-foreground'
                    : 'border-border bg-card text-muted-foreground hover:border-accent/40'
                }`}
              >
                <span className="text-base font-semibold">{opt.label}</span>
                <span className="text-xs opacity-60">{opt.sub}</span>
              </button>
            ))}
          </div>
          <input type="hidden" {...register('gender', { required: true })} />
        </div>

        {/* Privacy note */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary/60">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0 mt-0.5">
            <circle cx="8" cy="8" r="7" stroke="var(--muted-foreground)" strokeWidth="1.2" />
            <path d="M8 7v4M8 5.5v.5" stroke="var(--muted-foreground)" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <p className="text-xs text-muted-foreground leading-relaxed">
            你的出生資料僅用於計算五行比例，不會與第三方共享。
            分析完成後，你可以選擇是否儲存結果。
          </p>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isAnalyzing}
          className="w-full h-14 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-wide transition-all duration-200 hover:bg-foreground/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 mt-2"
        >
          {isAnalyzing ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="60" strokeDashoffset="20" />
              </svg>
              分析中...
            </>
          ) : (
            '分析我的五行色彩'
          )}
        </button>
      </form>
    </>
  );
}